import { useEffect, useState } from 'react';
import { Zap } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { checkUserMessageLimit, ChatLimitMode } from '@/services/rateLimitService';
import { Button } from '@/components/common/Button';
import { useNavigate } from 'react-router-dom';

interface PlanBannerProps {
  plan: string;
  mode?: ChatLimitMode;
}

export function PlanBanner({ plan, mode = 'funcionarios' }: PlanBannerProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState<number>(0);
  const [limit, setLimit] = useState<number>(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || plan !== 'free') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await checkUserMessageLimit(user.id, plan, mode);
        if (cancelled) return;
        setRemaining(res.remaining);
        setLimit(res.limit);
        setLoaded(true);
      } catch (error) {
        console.error('Error loading remaining messages:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [user, plan, mode]);

  if (plan !== 'free') return null;

  if (!loaded || !limit || !isFinite(limit)) return null;

  const usedPercentage = ((limit - remaining) / limit) * 100;

  const lowThreshold = Math.max(1, Math.ceil(limit * 0.15));
  const midThreshold = Math.max(2, Math.ceil(limit * 0.3));

  const getProgressColor = () => {
    if (remaining >= midThreshold) return 'bg-teal-500';
    if (remaining >= lowThreshold) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getBgColor = () => {
    if (remaining >= midThreshold) return 'bg-teal-50 border-teal-200';
    if (remaining >= lowThreshold) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const getTextColor = () => {
    if (remaining >= midThreshold) return 'text-teal-800';
    if (remaining >= lowThreshold) return 'text-amber-800';
    return 'text-red-800';
  };

  return (
    <div className={`${getBgColor()} border rounded-xl px-3 sm:px-4 py-2 sm:py-3 shadow-sm animate-in fade-in duration-300`}>
      <div className="flex items-center gap-2 sm:gap-4">
        <Zap className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${remaining >= lowThreshold ? 'text-amber-500' : 'text-red-500'}`} />

        <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
             <span className={`text-xs sm:text-sm font-medium ${getTextColor()} truncate`}>
               <span className="hidden sm:inline">Te quedan </span>{remaining} de {limit}<span className="hidden sm:inline"> mensajes este mes</span>
             </span>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              size="sm"
              className="flex-shrink-0 text-xs h-7 sm:h-8 px-2 sm:px-3"
            >
              <span className="hidden sm:inline">Ver planes</span>
              <span className="sm:hidden">Planes</span>
            </Button>
          </div>

          <div className="relative h-1.5 sm:h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={`h-full transition-all ${getProgressColor()}`}
              style={{ width: `${usedPercentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
